/**
 * iter91 — RevertStatusButton
 *
 * Bouton "↩️ Retour étape" affiché dans l'en-tête de l'OR lorsque le statut est
 * `travaux_effectue` ou `pret`. Reculer l'OR à l'étape précédente avec motif
 * optionnel et trace d'audit.
 */
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Undo2, Loader2 } from "lucide-react";
import api from "@/lib/api";

const ELIGIBLE_STATUSES = new Set(["pret", "travaux_effectue"]);

export default function RevertStatusButton({ or, onRefresh }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  if (!or || !ELIGIBLE_STATUSES.has(or.statut)) return null;

  const handleRevert = async () => {
    setBusy(true);
    try {
      await api.patch(`/or/${or.id}/revert-status`, { reason: reason.trim() });
      toast.success("Statut remis à l'étape précédente");
      setOpen(false);
      setReason("");
      if (onRefresh) await onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Retour étape impossible");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button
        size="sm"
        variant="outline"
        onClick={() => setOpen(true)}
        data-testid="revert-status-btn"
        className="border-amber-300 text-amber-700 hover:bg-amber-50"
        title="Reculer l&apos;OR à l&apos;étape précédente"
      >
        <Undo2 className="w-3.5 h-3.5 mr-1.5" /> Retour étape
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="revert-status-modal">
          <DialogHeader>
            <DialogTitle>Reculer l&apos;étape de l&apos;OR</DialogTitle>
            <DialogDescription>
              L&apos;OR passera du statut actuel à l&apos;étape précédente. Vous pouvez
              renseigner un motif (optionnel, conservé dans l&apos;audit).
            </DialogDescription>
          </DialogHeader>
          <Input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Motif (optionnel)"
            data-testid="revert-reason-input"
            className="mt-2"
          />
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setOpen(false)}
              data-testid="revert-cancel"
            >
              Annuler
            </Button>
            <Button
              onClick={handleRevert}
              disabled={busy}
              data-testid="revert-confirm"
              className="bg-amber-600 hover:bg-amber-700"
            >
              {busy ? (
                <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
              ) : (
                <Undo2 className="w-4 h-4 mr-1.5" />
              )}
              Confirmer le retour
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
